<template>
  <div class="task-schedule">
    <div class="date-row">
      <div class="date-block">
        <label class="label">Дата</label>
        <input
            v-model="selectedDate"
            type="date"
            class="input"
            :min="todayString"
        />
      </div>

      <div class="date-block">
        <label class="label">Начало</label>
        <select v-model="startTime" class="input">
          <option
              v-for="time in startTimeOptions"
              :key="`start-${time}`"
              :value="time"
          >
            {{ time }}
          </option>
        </select>
      </div>
      <div class="date-block">
        <label class="label">Завершение</label>
        <select v-model="endTime" class="input">
          <option
              v-for="time in endTimeOptions"
              :key="`end-${time}`"
              :value="time"
          >
            {{ time }}
          </option>
        </select>
      </div>
    </div>

    <div class="date-block">
      <label class="label">Повторяемость</label>
      <select v-model="repeatType" class="input">
        <option value="once">Не повторяется</option>
        <option value="daily">Каждый день</option>
        <option value="weekly">Каждую неделю</option>
        <option value="monthly">Каждый месяц</option>
      </select>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'

export default {
  name: 'TaskSchedulePicker',
  props: {
    modelValue: {
      type: Object,
    },
    initialDate: {
      type: String,
      default: '',
    },
    futureTask: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const selectedDate = ref('')
    const startTime = ref('')
    const endTime = ref('')
    const repeatType = ref('once')

    const STEP_MINUTES = 10
    const MIN_AHEAD_MINUTES = 10
    const NEAR_NOW_THRESHOLD_MINUTES = 30
    const FUTURE_THRESHOLD_MINUTES = 20

    const pad = (value) => String(value).padStart(2, '0')

    const formatDateLocal = (date) => {
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    }

    const todayString = computed(() => formatDateLocal(new Date()))

    const clampDateToTodayOrLater = (dateString) => {
      if (!dateString) {
        return todayString.value
      }

      return dateString < todayString.value
          ? todayString.value
          : dateString
    }

    const timeToMinutes = (time) => {
      const [hours, minutes] = time.split(':').map(Number)
      return hours * 60 + minutes
    }

    const minutesToTime = (totalMinutes) => {
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      return `${pad(hours)}:${pad(minutes)}`
    }

    const roundUpToNextStep = (date, step = STEP_MINUTES) => {
      const result = new Date(date)
      result.setSeconds(0, 0)

      const minutes = result.getMinutes()
      const remainder = minutes % step

      if (remainder !== 0) {
        result.setMinutes(minutes + (step - remainder))
      }

      return result
    }

    const getEarliestStartToday = () => {
      const now = new Date()
      const minAhead = props.futureTask ? (FUTURE_THRESHOLD_MINUTES * 60 * 1000) : 0;
      const minStart = new Date(now.getTime() + minAhead)

      return roundUpToNextStep(minStart)
    }

    const generateTimeOptions = () => {
      const options = []

      for (let minutes = 0; minutes < 24 * 60; minutes += STEP_MINUTES) {
        options.push(minutesToTime(minutes))
      }

      return options
    }

    const allTimeOptions = computed(() => generateTimeOptions())

    const isTodaySelected = computed(() => selectedDate.value === todayString.value)

    const earliestStartTimeToday = computed(() => {
      const earliest = getEarliestStartToday()
      return `${pad(earliest.getHours())}:${pad(earliest.getMinutes())}`
    })

    const startTimeOptions = computed(() => {
      let options = allTimeOptions.value.filter(
          (time) => timeToMinutes(time) < 24 * 60 - STEP_MINUTES
      )

      if (isTodaySelected.value) {
        const minMinutes = timeToMinutes(earliestStartTimeToday.value)
        options = options.filter((time) => timeToMinutes(time) >= minMinutes)
      }

      return options
    })

    const endTimeOptions = computed(() => {
      if (!startTime.value) {
        return []
      }

      const startMinutes = timeToMinutes(startTime.value)

      return allTimeOptions.value.filter((time) => timeToMinutes(time) > startMinutes)
    })

    const startDateTime = computed(() => {
      if (!selectedDate.value || !startTime.value) {
        return null
      }

      return new Date(`${selectedDate.value}T${startTime.value}:00`)
    })

    const isPlannedTask = computed(() => {
      if (!startDateTime.value) {
        return false;
      }

      if (props.futureTask && selectedDate.value === todayString.value) {
        return true;
      }

      const now = new Date()
      const diffMinutes = (startDateTime.value.getTime() - now.getTime()) / (1000 * 60)

      if (selectedDate.value !== todayString.value) {
        return true;
      }

      return diffMinutes > NEAR_NOW_THRESHOLD_MINUTES
    })

    const emitValue = () => {
      emit('update:modelValue', {
        date: selectedDate.value,
        start_time: startTime.value,
        end_time: endTime.value,
        repeat_type: repeatType.value,
        isCurrentTask: !isPlannedTask.value,
        isPlannedTask: isPlannedTask.value,
      })
    }

    const syncStartTime = () => {
      const options = startTimeOptions.value

      if (!options.length) {
        startTime.value = ''
        return
      }

      if (!options.includes(startTime.value)) {
        startTime.value = options[0]
      }
    }

    const syncEndTime = () => {
      const options = endTimeOptions.value

      if (!options.length) {
        endTime.value = ''
        return
      }

      if (!options.includes(endTime.value)) {
        endTime.value = options[0]
      }
    }

    const initFromExternalOrNow = () => {
      const safeDate = clampDateToTodayOrLater(props.initialDate);

      selectedDate.value = safeDate;

      if (safeDate === todayString.value) {
        const earliest = getEarliestStartToday()
        const end = new Date(earliest.getTime() + STEP_MINUTES * 60 * 1000)

        startTime.value = `${pad(earliest.getHours())}:${pad(earliest.getMinutes())}`
        endTime.value = `${pad(end.getHours())}:${pad(end.getMinutes())}`
      } else {
        startTime.value = '09:00'
        endTime.value = '09:30'
      }

      repeatType.value = 'once'
    }

    onMounted(() => {
      initFromExternalOrNow()
      syncStartTime()
      syncEndTime()
      emitValue()
    })

    watch(
        () => props.initialDate,
        (newDate) => {
          if (!newDate) {
            return
          }

          selectedDate.value = clampDateToTodayOrLater(newDate)
          syncStartTime()
          syncEndTime()
          emitValue()
        }
    )

    watch(selectedDate, (newDate) => {
      const safeDate = clampDateToTodayOrLater(newDate)

      if (safeDate !== newDate) {
        selectedDate.value = safeDate
        return
      }

      syncStartTime()
      syncEndTime()
      emitValue()
    })

    watch(startTime, () => {
      syncEndTime()
      emitValue()
    })

    watch([endTime, repeatType], () => {
      emitValue()
    })

    return {
      selectedDate,
      startTime,
      endTime,
      repeatType,
      startTimeOptions,
      endTimeOptions,
      todayString,
    }
  },
}
</script>

<style scoped>
.task-schedule {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .input {
    height: 40px;
    padding: 8px 12px;
    font-size: 14px;
    border-radius: 8px;
  }
}

.date-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.date-block {
  display: flex;
  column-gap: 10px;
  align-items: center;
}

.label {
  font-size: 14px;
  font-weight: 600;
}
</style>